/**
 * Нийлүүлэгч нэмэх · засах · устгах/архивлах.
 *
 * Цорын ганц дуудагч нь «Тохиргоо → Нийлүүлэгч» (`pages/setup/suppliers`).
 * Тусдаа файлд байгаа шалтгаан нь устгах/архивлах ДҮРЭМ хуудасны
 * зурагтай хольцолдохгүй байх — 10 жилийн мөрдөх гинжийг хамгаалдаг
 * логик тул нүдэнд ил, нэг дор байх ёстой.
 *
 * ⚠️ УСТГАХ ЭСЭХИЙГ ТҮҮХ ШИЙДНЭ:
 *      · аялал, гутал БАЙХГҮЙ → `DELETE`, бүрмөсөн устана
 *      · түүхтэй             → архивлана (`isActive = false`)
 *    Түүхтэйг бодитоор устгах боломжгүй: `items`, `purchases` дээрх
 *    FK зөвшөөрөхгүй, зөвшөөрсөн ч 10 жилийн баталгааны мөрдөх гинж
 *    тасарна (CLAUDE.md №5). Сервер 409 буцаана.
 *
 * ⚠️ Баталгааны хэллэгийг СЕРВЕР шалгана. Энд шалгаж байгаа нь зөвхөн
 *    товчийг идэвхгүй болгох — `curl`-ээр тойрч гарвал сервер мөн
 *    адил татгалзана.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isValidSupplierPrefix } from '@boots/domain';
import { SUPPLIER_ARCHIVE_PHRASE as ARCHIVE_PHRASE } from '@boots/schema';
import { api, type Envelope } from '../lib/api';
import { Button, ErrorNote, Field, Input, Modal, Select, Spinner } from './ui';

export interface SupplierRow {
  id: string;
  code: string;
  barcodePrefix: string | null;
  name: string;
  type: string;
  region: string | null;
  city: string | null;
  contact?: string | null;
  isActive: boolean;
}

interface Usage {
  purchases: number;
  items: number;
  hasHistory: boolean;
  canDelete: boolean;
}

/**
 * Нийлүүлэгчийн жагсаалт өөрчлөгдөхөд шинэчлэх ёстой БҮХ query.
 * Нэг газар — шинэ дэлгэц нэмэхэд энд нэмнэ, тарааж бичихгүй.
 */
export function useSupplierRefresh(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['ref-suppliers'] }),
      qc.invalidateQueries({ queryKey: ['suppliers-report'] }),
      qc.invalidateQueries({ queryKey: ['dashboard'] }),
    ]);
  };
}

export function SupplierForm({ supplier, onClose, onDone }: {
  supplier: SupplierRow | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const isNew = !supplier;
  const [code, setCode] = useState(supplier?.code ?? '');
  const [prefix, setPrefix] = useState(supplier?.barcodePrefix ?? '');
  const [name, setName] = useState(supplier?.name ?? '');
  const [type, setType] = useState(supplier?.type ?? 'INDIVIDUAL');
  const [region, setRegion] = useState(supplier?.region ?? '');
  const [city, setCity] = useState(supplier?.city ?? '');
  const [contact, setContact] = useState(supplier?.contact ?? '');
  const [danger, setDanger] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const common = {
        barcodePrefix: prefix || null,
        name,
        type,
        region: region.trim() || null,
        city: city.trim() || null,
        contact: contact.trim() || null,
      };
      return isNew
        ? api('/ref/suppliers', { method: 'POST', body: { code: code.toUpperCase(), ...common } })
        : api(`/ref/suppliers/${supplier.id}`, { method: 'PATCH', body: common });
    },
    onSuccess: onDone,
  });

  const reactivate = useMutation({
    mutationFn: () =>
      api(`/ref/suppliers/${supplier?.id}`, { method: 'PATCH', body: { isActive: true } }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={isNew ? 'Шинэ нийлүүлэгч' : `Нийлүүлэгч ${supplier.code}`}
      subtitle={isNew ? undefined : supplier.name}
      onClose={onClose}
      footer={
        <>
          {/* ⚠️ Устгах нь ЗҮҮН талд, Хадгалахаас хол — санамсаргүй дарахгүй */}
          {isNew ? null : (
            <Button variant="ghost" className="mr-auto text-alert-ink" onClick={() => setDanger(true)}>
              Устгах…
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!name || (isNew && !code) || (prefix !== '' && !isValidSupplierPrefix(prefix)) || save.isPending}
          >
            {save.isPending ? 'Хадгалж байна…' : 'Хадгалах'}
          </Button>
        </>
      }
    >
      {isNew ? (
        <Field label="Код">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="SUP-013"
            className="num"
          />
        </Field>
      ) : null}

      {!isNew && !supplier.isActive ? (
        <div className="flex flex-wrap items-center gap-3 rounded-well bg-warn-bg px-3.5 py-3">
          <span className="text-[13px] text-warn-ink">
            Энэ нийлүүлэгч <b>архивлагдсан</b> — шинэ аялалд сонгогдохгүй.
          </span>
          <Button
            variant="ghost"
            className="ml-auto"
            onClick={() => reactivate.mutate()}
            disabled={reactivate.isPending}
          >
            {reactivate.isPending ? 'Сэргээж байна…' : 'Буцааж идэвхжүүлэх'}
          </Button>
        </div>
      ) : null}

      <Field label="Нэр"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
      {/*
        ⚠️ Шошгон дээр аль хэдийн хэвлэгдсэн угтвартай ЯГ таарах ёстой —
           эхний үлдэгдлийн бүртгэл баркодын эхний 2 оронгоор нийлүүлэгчийг
           олно. Буруу бол гутал өөр нийлүүлэгчид бүртгэгдэнэ.
      */}
      <Field label="Баркодын угтвар (шошгоны эхний 2 орон)">
        <Input
          value={prefix}
          onChange={(e) => setPrefix(e.target.value.replace(/\D/g, '').slice(0, 2))}
          placeholder="44"
          inputMode="numeric"
          className="num w-[90px]"
        />
      </Field>
      <Field label="Төрөл">
        <Select
          value={type}
          onChange={setType}
          options={[
            { value: 'INDIVIDUAL', label: 'Хувь хүн' },
            { value: 'COMPANY', label: 'Байгууллага' },
          ]}
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Бүс"><Input value={region} onChange={(e) => setRegion(e.target.value)} /></Field>
        <Field label="Хот"><Input value={city} onChange={(e) => setCity(e.target.value)} /></Field>
      </div>
      <Field label="Холбоо барих">
        <Input value={contact} onChange={(e) => setContact(e.target.value)} />
      </Field>

      {save.error ? <ErrorNote>{(save.error as Error).message}</ErrorNote> : null}
      {reactivate.error ? <ErrorNote>{(reactivate.error as Error).message}</ErrorNote> : null}

      {danger && supplier ? (
        <SupplierDanger
          supplier={supplier}
          onClose={() => setDanger(false)}
          onDone={() => { setDanger(false); onDone(); }}
        />
      ) : null}
    </Modal>
  );
}

export function SupplierDanger({ supplier, onClose, onDone }: {
  supplier: SupplierRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [phrase, setPhrase] = useState('');

  const usage = useQuery({
    queryKey: ['supplier-usage', supplier.id],
    queryFn: () => api<Envelope<Usage>>(`/ref/suppliers/${supplier.id}/usage`),
  });

  const u = usage.data?.data;
  const ok = u?.canDelete === true || phrase.trim() === ARCHIVE_PHRASE;

  const act = useMutation({
    mutationFn: () =>
      u?.canDelete
        ? api(`/ref/suppliers/${supplier.id}`, { method: 'DELETE' })
        : api(`/ref/suppliers/${supplier.id}`, {
            method: 'PATCH',
            body: { isActive: false, confirm: phrase.trim() },
          }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={`${supplier.code} — ${u?.canDelete ? 'устгах' : 'архивлах'}`}
      subtitle={supplier.name}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button variant="danger" onClick={() => act.mutate()} disabled={!ok || act.isPending}>
            {act.isPending ? 'Гүйцэтгэж байна…' : u?.canDelete ? 'Бүрмөсөн устгах' : 'Архивлах'}
          </Button>
        </>
      }
    >
      {usage.isPending ? (
        <Spinner label="Түүх шалгаж байна…" />
      ) : u?.canDelete ? (
        <p className="text-[13px]">
          Энэ нийлүүлэгчид <b>аялал ч, гутал ч бүртгэгдээгүй</b>. Бүрмөсөн устгана —
          буцаах боломжгүй.
        </p>
      ) : (
        <>
          <p className="text-[13px]">
            Энэ нийлүүлэгчид <b>{u?.purchases} аялал</b>, <b>{u?.items} гутал</b> бүртгэлтэй.
            Тиймээс устгах боломжгүй — устгавал баталгааны мөрдөх гинж тасарна.
          </p>
          <p className="text-[13px] text-ink-2">
            Оронд нь <b>архивлана</b>: шинэ аялалд сонгогдохоо болино, түүх бүтэн үлдэнэ.
            Хожим буцааж идэвхжүүлж болно.
          </p>
          <Field label={`Баталгаажуулахын тулд «${ARCHIVE_PHRASE}» гэж бичнэ үү`}>
            <Input
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              placeholder={ARCHIVE_PHRASE}
              autoComplete="off"
            />
          </Field>
        </>
      )}
      {act.error ? <ErrorNote>{(act.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}
